import * as React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
// NOTE: Site-level components such as Header and Footer should be rendered from the app root
// (e.g. `src/app.jsx`) to avoid duplication across routes. Do not import the Footer or Header
// components directly in page-level/navigation components. This file follows that rule.
import About from './about/about';
import Home from './home.tsx';
import Contact from './contact.tsx';
import NotFound from './notFound.tsx';
import { Navigate } from 'react-router-dom';
import ApiClient from '../Services/ApiClient';
import FlashComponent from './topVideo.tsx';
import PatientsWrapper from './patients/patientsWrapper.tsx';
import PatientMeetings from './patients/PatientMeetings';
import PatientsProvider from '../store/patientsProvider';
import Meetings from './meetings/meetings.tsx';
import LoginWrapper from './login/loginWrapper.tsx';
import AuthContext from '../store/authContext.tsx';
import PrivateRoute from '../components/privateRoute.tsx';
import AdminRoute from '../components/adminRoute.tsx';
import { useResources, useComponentResources } from '../store/resourcesProvider';
import Item from './items/item.tsx'; // Adjust the path as needed
import ItemsAdmin from './items/admin.tsx';
import LeadsPage from './leads/leads';
import ResourcesAdmin from './admin/resourcesAdmin';
import SitesAdmin from './admin/sitesAdmin';
import SiteSettingsManager from './admin/siteSettingsManager';
const LeadsPageLazy = React.lazy(() => import('../pages/leads'));
import Register from './login/register.tsx';
import ApproveUser from './login/approveUser';
import ForgotPassword from './login/forgotPassword.tsx';
import ResetPassword from './login/resetPassword.tsx';
import AccessibilityStatement from './accessibility/AccessibilityStatement';
const UsersPageLazy = React.lazy(() => import('./users/users'));

import '../style/nav.css'
import '../style/navbar.css'

const Navigation = () => {
    const { isLoggedIn, logout, isTokenExpired, isAdmin, isSuperAdmin, token } = React.useContext(AuthContext);
    // Call useResources exactly once, unconditionally
    const resources = useResources();
    const [canRegister, setCanRegister] = React.useState(false);

    React.useEffect(() => {
        const load = async () => {
            try {
                // Use the server endpoint that returns a RegistrationAvailability object
                // use dedicated registration availability endpoint
                const res = await ApiClient.get(`/registration/availability`);
                // res should be { canRegister: bool, remaining: number|null, reason: string|null }
                console.debug('[Navigation] registration availability', { siteId: resources.siteId, res });
                setCanRegister(!!(res && res.canRegister));
            } catch (e) {
                console.debug('[Navigation] failed to fetch registration availability', e);
                setCanRegister(false);
            }
        };
        load();
    }, [resources.siteId]);

    const { culture, setCulture } = resources;
    // Preload nav labels and read pageRes (includes userId automatically)
    const pageRes = useComponentResources('Nav', ['Nav.Home','Nav.About','Nav.Contact','Nav.Patients','Nav.Meetings','Nav.Users','Nav.Items','Nav.Resources','Nav.ResourcesLanguage','Nav.ResourcesHeb','Nav.ResourcesEng','Nav.Login','Nav.Logout','Nav.Register']);

    const checkTokenExpiration = () => {
        // Prefer token from auth context (keeps in-sync with provider); fall back to localStorage
        const t = token ?? localStorage.getItem('token');
        return isTokenExpired(t);
    };
    return (
        <Router>
            <nav className={`navbar`}>
                <div className="nav-inner">
                    {/* No hamburger in production — always show nav links */}
                    <div className="nav-brand" aria-hidden>
                        {/* optional brand area - keep empty or add logo if needed */}
                    </div>
                    <div className={`nav-links`}>
                        <Link className="navbar-link" to="/">{pageRes['Nav.Home'] ?? resources.get('Nav.Home','ראשי')}</Link>
                        <Link className="navbar-link" to="/about">{pageRes['Nav.About'] ?? resources.get('Nav.About','אודות')}</Link>
                        <Link className="navbar-link" to="/contact">{pageRes['Nav.Contact'] ?? resources.get('Nav.Contact','צור קשר')}</Link>

                        {isLoggedIn && !checkTokenExpiration() && (
                            <>
                                <Link className="navbar-link" to="/patients">{pageRes['Nav.Patients'] ?? resources.get('Nav.Patients','מטופלים')}</Link>
                                <Link className="navbar-link" to="/meetings">{pageRes['Nav.Meetings'] ?? resources.get('Nav.Meetings','פגישות')}</Link>
                            </>
                        )}

                        {isLoggedIn && !checkTokenExpiration() && (isAdmin || isSuperAdmin) && (
                            <>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                  
                                    <Link className="navbar-link" to="/users">{pageRes['Nav.Users'] ?? resources.get('Nav.Users','משתמשים')}</Link>
                                    <Link className="navbar-link" to="/items">{pageRes['Nav.Items'] ?? resources.get('Nav.Items','פריטים')}</Link>
                                    <Link className="navbar-link" to="/resources">{pageRes['Nav.Resources'] ?? resources.get('Nav.Resources','תוכן')}</Link>
                                    <select value={culture || ''} onChange={e => setCulture(e.target.value || null)} style={{marginLeft:12}}>
                                        <option value="">{pageRes['Nav.ResourcesLanguage'] ?? resources.get('Nav.ResourcesLanguage', 'עבור שפה')}</option>
                                        <option value="he-IL">{pageRes['Nav.ResourcesHeb'] ?? resources.get('Nav.ResourcesHeb', 'עברית')}</option>
                                        <option value="en-US">{pageRes['Nav.ResourcesEng'] ?? resources.get('Nav.ResourcesEng', 'ENGLISH')}</option>
                                    </select>
                                        {isSuperAdmin && <Link className="navbar-link" to="/sites/settings" style={{ marginLeft: 8, color: '#c00', fontWeight: 'bold' }}>Site Settings</Link>}
                                    <Link className="navbar-link" to="/leads">Leads</Link>
                                </span>
                                {isSuperAdmin && <span style={{ marginLeft: 12, padding: '2px 6px', background: '#c00', color: 'white', borderRadius: 12, fontSize: 12 }}>SuperAdmin</span>}
                            </>
                        )}

                        {!isLoggedIn || checkTokenExpiration()
                             ? <Link className="navbar-link" to="/login">{pageRes['Nav.Login'] ?? resources.get('Nav.Login','התחברות')}</Link> : null}
                        {/* Show register link only when registration is available */}
                        {!isLoggedIn && canRegister && (
                            <Link className="navbar-link" to="/register">{resources.get('Nav.Register','הרשמה')}</Link>
                        )}
                        {isLoggedIn && !checkTokenExpiration() 
                            ?
                            <button className="btn-on-dark" onClick={() => { logout(); }} >{pageRes['Nav.Logout'] ?? resources.get('Nav.Logout','התנתק')}</button>
                            : null}
                    </div>
                </div>
            </nav>
            <div>
                <FlashComponent />
            </div>

            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route element={<PrivateRoute isAuthenticated={isLoggedIn} />}>
                    {/* Patients and Meetings are available to any authenticated user; wrap with PatientsProvider */}
                    <Route path="/patients" element={
                        <PatientsProvider>
                            <PatientsWrapper />
                        </PatientsProvider>
                    } />
                    <Route path="/patient/meetings" element={<PatientsProvider><PatientMeetings /></PatientsProvider>} />
                    <Route path="/meetings" element={
                        <PatientsProvider>
                            <Meetings />
                        </PatientsProvider>
                    } />

                    {/* Admin-only routes */}
                    <Route element={<AdminRoute />}>
                        <Route path="/users" element={isAdmin ? <React.Suspense fallback={<div>Loading...</div>}><UsersPageLazy /></React.Suspense> : <NotFound />} />
                        <Route path="/items" element={isAdmin ? <ItemsAdmin /> : <NotFound />} />
                        <Route path="/resources" element={isAdmin ? <ResourcesAdmin /> : <NotFound />} />
                        <Route path="/sites" element={isAdmin ? <SitesAdmin /> : <NotFound />} />
                        <Route path="/sites/settings" element={isSuperAdmin ? <SiteSettingsManager /> : <NotFound />} />
                        <Route path="/admin/approve" element={isAdmin ? <ApproveUser /> : <NotFound />} />
                    </Route>
                        <Route path="/leads" element={isAdmin ? <React.Suspense fallback={<div>Loading...</div>}><LeadsPageLazy /></React.Suspense> : <NotFound />} />
                </Route>
                <Route path="/login" element={<LoginWrapper />} />
                <Route path="/item/:id" element={<Item />} />
                <Route path="/items" element={isAdmin ? <ItemsAdmin /> : <NotFound />} />
                <Route path="/leads" element={isAdmin ? <LeadsPage /> : <NotFound />} />
                <Route path="/resources" element={isAdmin ? <ResourcesAdmin /> : <NotFound />} />
                {/* Always allow client to render the register page; Register component enforces availability checks itself. */}
                <Route path="/register" element={<Register />} />
                <Route path="/accessibility" element={<AccessibilityStatement />} />
                <Route path="/admin/approve" element={<ApproveUser />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                {/* Route should not include query string — ResetPassword reads token from URL query (e.g. /reset-password?token=...) */}
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
        </Router>
    );
}

export default Navigation;